import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface SiteSettings {
  initialDelayMs: number;
  categoryDelayMs: number;
  logoUrl: string;
  faviconUrl: string;
  siteName: string;
  siteSubtitle: string;
  instagramUrl: string;
  facebookUrl: string;
  pinterestUrl: string;
  youtubeUrl: string;
}

export const defaultSiteSettings: SiteSettings = {
  initialDelayMs: 5000,
  categoryDelayMs: 5000,
  logoUrl: '',
  faviconUrl: '',
  siteName: 'miaudecasa',
  siteSubtitle: '',
  instagramUrl: '',
  facebookUrl: '',
  pinterestUrl: '',
  youtubeUrl: '',
};

function mapRow(row: Record<string, unknown> | null): SiteSettings {
  if (!row) return { ...defaultSiteSettings };
  return {
    initialDelayMs: (row.initial_delay_ms as number) ?? defaultSiteSettings.initialDelayMs,
    categoryDelayMs: (row.category_delay_ms as number) ?? defaultSiteSettings.categoryDelayMs,
    logoUrl: (row.logo_url as string) ?? defaultSiteSettings.logoUrl,
    faviconUrl: (row.favicon_url as string) ?? defaultSiteSettings.faviconUrl,
    siteName: (row.site_name as string) ?? defaultSiteSettings.siteName,
    siteSubtitle: (row.site_subtitle as string) ?? defaultSiteSettings.siteSubtitle,
    instagramUrl: (row.instagram_url as string) ?? defaultSiteSettings.instagramUrl,
    facebookUrl: (row.facebook_url as string) ?? defaultSiteSettings.facebookUrl,
    pinterestUrl: (row.pinterest_url as string) ?? defaultSiteSettings.pinterestUrl,
    youtubeUrl: (row.youtube_url as string) ?? defaultSiteSettings.youtubeUrl,
  };
}

export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings>(defaultSiteSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();

      if (error) throw error;
      setSettings(mapRow(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async (updated: SiteSettings): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('site_settings')
        .upsert({
          id: 1,
          initial_delay_ms: updated.initialDelayMs,
          category_delay_ms: updated.categoryDelayMs,
          logo_url: updated.logoUrl,
          favicon_url: updated.faviconUrl,
          site_name: updated.siteName,
          site_subtitle: updated.siteSubtitle,
          instagram_url: updated.instagramUrl,
          facebook_url: updated.facebookUrl,
          pinterest_url: updated.pinterestUrl,
          youtube_url: updated.youtubeUrl,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
      setSettings(updated);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar configurações');
      return false;
    }
  };

  return { settings, saveSettings, loading, error, refetch: fetchSettings };
}